import { JwtPayload } from 'jwt-decode';
import { Action, Computed, Thunk } from 'easy-peasy';
import { User } from '../../api/apiTypes';
// eslint-disable-next-line import/no-cycle
import { StoreModel } from '../storeModel';

export type AuthorizedUser = {
  username: string;
  name: string;
  roles: UserRole[];
  partnerCode: string;
  partnerName: string;
};

export enum UserRole {
  SALESCONNECT_ADMIN = 'SALESCONNECT_ADMIN',
  PARTNER_ADMIN = 'PARTNER_ADMIN',
  PARTNER_WAREHOUSE_OPERATIONS = 'PARTNER_WAREHOUSE_OPERATIONS',
  PARTNER_USER = 'PARTNER_USER',
}

export enum Permission {
  RevertOrderStatus = 'revertOrderStatus',
  ProductImport = 'productImport',
  KauflandImport = 'kauflandImport',
  AllegroImport = 'allegroImport',
  LabelCreation = 'labelCreation',
  UpdateStocksAndPrices = 'updateStocksAndPrices',
  BatchUpdate = 'batchUpdate',
  Orders = 'orders',
  Shipments = 'shipments',
  Products = 'products',
  Pricing = 'pricing',
  DefaultCommissionPricelist = 'defaultCommissionPricelist',
  UserProfile = 'userProfiles',
  PartnerSetupOverview = 'partnerSetupOverview',
  PartnerSetupOverviewCreate = 'partnerSetupOverview.create',
  PartnerSetup = 'partnerSetup',
  PartnerSetupWorkflowStatus = 'partnerSetup.workflowStatus',
  EshopSetupOverview = 'eshopSetupOverview',
  EshopSetup = 'eshopSetup',
  UserSetup = 'userSetup',
  UserSetupCreate = 'userSetup.create',
  UserSetupManage = 'userSetup.manage',
  CarrierSetup = 'carrierSetup',
  UpdateParcelShopBranchId = 'updateParcelShopBranchId',
  FeedMorpher = 'feedMorpher',
  RemoveOrderError = 'removeOrderError',
  SenderGlnNumber = 'senderGlnNumber',
  PrecanceledOrders = 'precanceledOrders',
  PartnerSalesEntity = 'partnerSalesEntity',
  GlobalStockSwitch = 'globalStockSwitch',
}

export const rolePermissions = {
  SALESCONNECT_ADMIN: [
    Permission.RevertOrderStatus,
    Permission.ProductImport,
    Permission.KauflandImport,
    Permission.AllegroImport,
    Permission.LabelCreation,
    Permission.UpdateStocksAndPrices,
    Permission.BatchUpdate,
    Permission.Orders,
    Permission.Shipments,
    Permission.Products,
    Permission.UserProfile,
    Permission.PartnerSetupOverview,
    Permission.PartnerSetupOverviewCreate,
    Permission.PartnerSetup,
    Permission.PartnerSetupWorkflowStatus,
    Permission.EshopSetupOverview,
    Permission.EshopSetup,
    Permission.UserSetup,
    Permission.UserSetupCreate,
    Permission.UserSetupManage,
    Permission.CarrierSetup,
    Permission.UpdateParcelShopBranchId,
    Permission.FeedMorpher,
    Permission.RemoveOrderError,
    Permission.SenderGlnNumber,
    Permission.PrecanceledOrders,
    Permission.Pricing,
    Permission.DefaultCommissionPricelist,
    Permission.PartnerSalesEntity,
    Permission.GlobalStockSwitch,
  ],
  PARTNER_ADMIN: [
    Permission.ProductImport,
    Permission.KauflandImport,
    Permission.AllegroImport,
    Permission.LabelCreation,
    Permission.UpdateStocksAndPrices,
    Permission.BatchUpdate,
    Permission.Orders,
    Permission.Shipments,
    Permission.Products,
    Permission.UserProfile,
    Permission.UserSetup,
    Permission.PartnerSetup,
    Permission.EshopSetup,
    Permission.Pricing,
    Permission.GlobalStockSwitch,
    Permission.FeedMorpher,
  ],
  PARTNER_USER: [
    Permission.ProductImport,
    Permission.KauflandImport,
    Permission.AllegroImport,
    Permission.LabelCreation,
    Permission.UpdateStocksAndPrices,
    Permission.BatchUpdate,
    Permission.Orders,
    Permission.Shipments,
    Permission.Products,
    Permission.UserProfile,
    Permission.FeedMorpher,
  ],
  PARTNER_WAREHOUSE_OPERATIONS: [
    Permission.Orders,
    Permission.Products,
    Permission.LabelCreation,
    Permission.Shipments,
  ],
};

export type UserWithToken = {
  user: AuthorizedUser
  token: string,
  expires: number,
};

export interface TokenPayload extends JwtPayload {
  user: User
}

interface ChangePasswordPayload {
  username: string;
  password: string;
  newPassword: string;
}

export interface UserStoreModel {
  userTokens: UserWithToken[];
  currentUser: AuthorizedUser;
  login: Thunk<UserStoreModel, { username: string, password: string }, any, StoreModel>
  changePassword: Thunk<UserStoreModel, ChangePasswordPayload, any, StoreModel>
  logout: Action<UserStoreModel>;
  restore: Action<UserStoreModel>;
  setTokens: Action<UserStoreModel, UserWithToken[]>;
  changePartner: Action<UserStoreModel, string>;
  setCurrentUser: Action<UserStoreModel, AuthorizedUser>;
  hasPermission: Computed<UserStoreModel, (requiredPermission: Permission) => boolean>;
}
